'use strict';

/**
 * Pict-Provider-Markup
 * --------------------
 * One representation of an entity, used everywhere.
 *
 * A drawing set works because every sheet carries a mark, the mark is stable, and any sheet can
 * reference any other by it. WI-003 is always WI-003. Eighteen entities carry a per-plan-sheet
 * number (the descriptor NumberField), but until now that number only sorted a list and headed a
 * detail page, so nothing on screen could quote it. This turns those numbers into a designator and
 * builds the three representations every surface needs from one place:
 *
 *   designator()   the mark itself, WI-301
 *   mark()         the normalized record a title block or card renders
 *   tooltipMark()  the same, plus description, status, and cross-references
 *
 * The prefix is NOT invented here. It lives on the descriptor beside NumberField
 * (EntityDescriptors.js), which is where the rest of an entity's identity already lives, so a new
 * entity declares its own mark and this provider stays dumb. Two prefixes collide on first letter and
 * are resolved there by seniority: Feature keeps F so Feedback takes FB, and Deploy Request keeps DR
 * so Deliverable takes DLV. A test asserts they all stay unique, and it counts them, so adding one is
 * a deliberate act rather than a merge artifact.
 *
 * Nothing here talks to the network. It is pure formatting over records a caller already has.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libDescriptors = require('../EntityDescriptors.js');
// The mark itself is formed in EntityMark, not here. This provider requires pict, so the server can
// never load it -- and BlueprintRender draws the public shared page server-side. Keeping the
// formatter in a dependency-free module is what stops the two sides growing separate copies of the
// padding and prefix rules.
const libEntityMark = require('../EntityMark.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'Markup',
	Dependencies: [],
	// Three digits is the width a plan sheet reads best at (WI-006, not WI-6). Past 999 the number
	// simply grows -- padding is a floor, never a ceiling, so WI-1024 is untouched.
	NumberPad: 3
};

class PictProviderMarkup extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.numberPad = libEntityMark.NumberPad;
	}

	descriptor(pEntityKey) { return libEntityMark.descriptorFor(pEntityKey); }

	/** The prefix an entity marks its records with, or '' when it does not carry a number. */
	prefix(pEntityKey) { return libEntityMark.prefix(pEntityKey); }

	/**
	 * The mark: 'WI-301'. Accepts either the record or the bare number, because callers have one or the
	 * other depending on how much of the row they loaded.
	 */
	designator(pEntityKey, pRecordOrNumber) { return libEntityMark.designator(pEntityKey, pRecordOrNumber); }

	/** Read a mark back into what it points at: 'WI-301' -> { Entity:'WorkItem', Number:301 }. */
	parse(pDesignator) { return libEntityMark.parse(pDesignator); }

	/**
	 * The normalized record a title block or a card renders. Cells are ordered and already stringified,
	 * so a template never reaches back into the entity and every surface shows the same four things in
	 * the same order. A cell with nothing to say still renders, with a dash, because a missing cell
	 * would shift the ones after it and break the scan down a column.
	 */
	mark(pEntityKey, pRecord, pOptions)
	{
		let tmpRecord = pRecord || {};
		let tmpOptions = pOptions || {};
		let tmpDescriptor = this.descriptor(pEntityKey);
		let tmpCells = Array.isArray(tmpOptions.Cells) ? tmpOptions.Cells : [];
		return {
			Entity: pEntityKey,
			Designator: this.designator(pEntityKey, tmpRecord),
			Title: String(tmpRecord[(tmpDescriptor && tmpDescriptor.TitleField) || 'Title'] || ''),
			Kind: String(tmpRecord.Type || (tmpDescriptor && tmpDescriptor.Title) || pEntityKey),
			KindClass: 'ps-mark-' + String(pEntityKey).toLowerCase(),
			Icon: (tmpDescriptor && tmpDescriptor.Icon) || '',
			Status: String(tmpRecord.Status || ''),
			Cells: tmpCells.map((pCell) => ({ K: String(pCell.K || ''), V: this._cellValue(pCell.V) }))
		};
	}

	/** A cell always renders. An empty value becomes a dash so the columns below it stay aligned. */
	_cellValue(pValue)
	{
		let tmpText = (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue).trim();
		return tmpText ? tmpText : '—';
	}

	/**
	 * The loaded tooltip: the mark, plus what a person needs to decide without opening the record.
	 * Cross-references are marks themselves, which is the whole point -- the tooltip for WI-301 names
	 * F-167 and V-043, and those read the same everywhere else in the platform.
	 */
	tooltipMark(pEntityKey, pRecord, pOptions)
	{
		let tmpOptions = pOptions || {};
		let tmpMark = this.mark(pEntityKey, pRecord, tmpOptions);
		tmpMark.Description = String((pRecord || {}).Description || '');
		tmpMark.References = (Array.isArray(tmpOptions.References) ? tmpOptions.References : [])
			.map((pReference) => (typeof pReference === 'string')
				? pReference
				: this.designator(pReference.Entity, pReference.Number))
			.filter((pReference) => pReference);
		return tmpMark;
	}
}

module.exports = PictProviderMarkup;
module.exports.default_configuration = _DefaultProviderConfiguration;
